import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, Flame, Award, BookOpen, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import BadgePill from "@/components/BadgePill";

export default function MyClubs() {
    const { t } = useTranslation();
    const { user, loading } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user || typeof user !== "object") return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/clubs/me");
                if (alive) setData(data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
            }
        })();
        return () => { alive = false; };
    }, [user, loading]);

    if (loading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <Users size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {t("clubs.login_required")}
                </p>
                <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                    {t("nav.login")}
                </Link>
            </main>
        );
    }

    if (!data) {
        return <main className="min-h-[calc(100vh-73px)] max-w-5xl mx-auto px-6 py-24"><p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p></main>;
    }

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">{t("clubs.profile_eyebrow")}</p>
            <h1 className="font-display text-5xl md:text-6xl text-[var(--ink)] mb-4"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}>
                {t("clubs.profile_title")}
            </h1>

            <section className="grid grid-cols-2 md:grid-cols-3 gap-6 my-12 border-y border-[var(--border)] py-8" data-testid="profile-stats">
                <Stat icon={Trophy} label={t("clubs.total_xp")} value={data.total_xp} />
                <Stat icon={Award} label={t("clubs.total_badges")} value={data.badges?.length || 0} />
                <Stat icon={BookOpen} label={t("clubs.total_clubs")} value={data.memberships.length} />
            </section>

            {data.badges?.length > 0 && (
                <section className="mb-12">
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-4">{t("clubs.badges_collected")}</p>
                    <div className="flex flex-wrap gap-2">
                        {data.badges.map((b) => <BadgePill key={b} code={b} />)}
                    </div>
                </section>
            )}

            <h2 className="font-display text-3xl text-[var(--ink)] mb-6"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                {t("clubs.your_memberships")}
            </h2>
            {data.memberships.length === 0 ? (
                <div className="text-center py-12 border border-[var(--border-strong)] bg-[var(--bg-elevated)]">
                    <p className="text-[var(--ink-muted)] mb-6">{t("clubs.no_memberships")}</p>
                    <Link to="/clubs" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2.5 text-sm uppercase tracking-widest">
                        {t("clubs.browse")}
                    </Link>
                </div>
            ) : (
                <ul className="space-y-3">
                    {data.memberships.map((m, i) => (
                        <li key={m.club.id}>
                            <Link
                                to={`/clubs/${m.club.slug}`}
                                data-testid={`my-membership-${i}`}
                                className="flex items-center justify-between gap-4 border border-[var(--border)] hover:border-[var(--accent)] bg-[var(--bg-elevated)] px-5 py-4 transition-colors"
                            >
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--accent)] mb-1">
                                        {t(`clubs.kind_${m.club.kind}`)} · {m.club.language?.toUpperCase()}
                                    </p>
                                    <p className="font-display text-xl text-[var(--ink)] truncate"
                                       style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                                        {m.club.name}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-[var(--ink-muted)] whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1"><Trophy size={12} strokeWidth={1.5} /> {m.xp}</span>
                                    {m.streak > 0 && <span className="inline-flex items-center gap-1 text-[var(--accent)]"><Flame size={12} strokeWidth={1.5} /> {m.streak}</span>}
                                    <span className="inline-flex items-center gap-1"><BookOpen size={12} strokeWidth={1.5} /> {m.cycles_completed}</span>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </main>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1 inline-flex items-center gap-1.5">
                <Icon size={11} strokeWidth={1.5} /> {label}
            </p>
            <p className="font-display text-4xl text-[var(--ink)] font-mono-num"
               style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                {value ?? 0}
            </p>
        </div>
    );
}
